import React, { FunctionComponent, useState } from "react";
import {
  Button,
  Grid2 as Grid,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Box,
  CardHeader,
  Stack,
  CardActions,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Zoom,
  Alert,
  AlertTitle,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  ArrowBackIos,
  Close,
  Delete,
  DeleteForever,
  EmojiEvents,
  Save,
  SportsScore,
  WorkspacePremium,
} from "@mui/icons-material";
import { Car, CarPosition } from "../models/car";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import TextInput from "../controls/TextInput";
import validationSchema from "./validationSchema";
import { yupResolver } from "@hookform/resolvers/yup";
import ListInput from "../controls/ListInput";
import ImagesInput from "../controls/ImagesInput";
import { CarService } from "../services/car-service";
import { useAppDispatch } from "../app/hooks";
import { addCar, deleteCar, updateCar } from "../slices/cars-slice";
import { LoadingButton } from "@mui/lab";
import { ErrorResponse } from "../models/response";
import PositionInput from "../controls/PositionInput";
import CheckboxInput from "../controls/CheckboxInput";

export type FormInputs = {
  brand?: string;
  type?: string;
  number?: string;
  competition?: string;
  category?: string;
  categoryWinner?: boolean;
  year?: number;
  pilots?: string[];
  position?: CarPosition;
  manufacturer?: string;
  reference?: string;
  details?: string;
  storageLocation?: string;
  purchasePrice?: number;
  owner?: string;
  photos?: string[];
};

type CarFromProps = {
  car: Car;
};

const CarForm: FunctionComponent<CarFromProps> = ({ car }) => {
  const { id: carId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAddMode = !carId;

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState<boolean>(false);
  const [deleteStatus, setDeleteStatus] = useState<{
    inProgress: boolean;
    errors: string[];
  }>({ inProgress: false, errors: null });
  const [globalErrors, setGlobalErrors] = useState<string[]>();

  const cancelEdit = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

    navigate(
      isAddMode ? `/cars${queryParams}` : `/cars/${carId}${queryParams}`,
    );
  };

  const closeEdit = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

    navigate(`/cars${queryParams}`);
  };

  const formMethods = useForm<FormInputs>({
    defaultValues: {
      photos: car?.photos || [],
      brand: car?.brand || "",
      type: car?.type || "",
      number: car?.number || "",
      competition: car?.competition || "",
      category: car?.category || "",
      categoryWinner: car?.categoryWinner || false,
      year: car?.year,
      position: car?.position || undefined,
      pilots: car?.pilots || [],
      manufacturer: car?.manufacturer || "",
      reference: car?.reference || "",
      details: car?.details || "",
      storageLocation: car?.storageLocation || "",
      purchasePrice: car?.purchasePrice,
      owner: car?.owner || "",
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";

    try {
      const photos = await Promise.all(
        data.photos.map(async (photo) => {
          if (photo.startsWith("blob:")) {
            return await CarService.uploadImage(photo);
          } else {
            return photo;
          }
        }),
      );

      if (isAddMode) {
        const newCar: Car = { status: "Nouveau", ...data, photos };
        await CarService.createCar(newCar).then((car) => {
          dispatch(addCar(car));
          navigate(`/cars/${car._id}${queryParams}`);
        });
      } else {
        const updatedCar = { ...car, ...data, photos };
        await CarService.updateCar(carId, updatedCar).then((car) => {
          dispatch(updateCar(car));
          navigate(`/cars/${car._id}${queryParams}`);
        });
      }
    } catch (e) {
      const errorResponse = e as ErrorResponse;
      setGlobalErrors(errorResponse.errors.map((err) => err.msg));
    }
  };

  const handleDelete = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : "";
    try {
      setDeleteStatus({ inProgress: true, errors: null });
      await CarService.deleteCar(carId).then(() => {
        dispatch(deleteCar(carId));
        navigate(`/cars${queryParams}`);
        setOpenDeleteConfirm(false);
        setDeleteStatus({ inProgress: false, errors: null });
      });
    } catch (e: any) {
      const errorResponse = e as ErrorResponse;
      setDeleteStatus({
        inProgress: false,
        errors: errorResponse.errors.map((err) => err.msg),
      });
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteConfirm(false);
    setDeleteStatus({
      inProgress: false,
      errors: null,
    });
  };

  const clearGlobalErrors = () => {
    setGlobalErrors(null);
  };

  return (
    <FormProvider {...formMethods}>
      <form
        noValidate
        onChange={clearGlobalErrors}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Card>
          <Grid container>
            <Grid size={12}>
              <CardHeader
                action={
                  <Stack direction="row" alignItems="center">
                    {!isAddMode && (
                      <IconButton aria-label="settings">
                        <ArrowBackIos onClick={() => cancelEdit()} />
                      </IconButton>
                    )}
                    <IconButton aria-label="settings">
                      <Close onClick={() => closeEdit()} />
                    </IconButton>
                  </Stack>
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CardMedia>
                <ImagesInput name="photos" fullWidth />
              </CardMedia>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextInput label="Marque" name="brand" fullWidth required />
                  </Grid>
                  <Grid size={{ sm: 9, xs: 12 }}>
                    <TextInput label="Type" name="type" fullWidth />
                  </Grid>
                  <Grid size={{ sm: 3, xs: 12 }}>
                    <TextInput label="N°" name="number" fullWidth />
                  </Grid>
                  <Grid size={6}>
                    <TextInput
                      label="Compétition"
                      name="competition"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={4}>
                    <TextInput label="Catégorie" name="category" fullWidth />
                  </Grid>
                  <Grid size={2} alignContent="center">
                    <CheckboxInput
                      name="categoryWinner"
                      checkedIcon={<WorkspacePremium color="secondary" />}
                      icon={<WorkspacePremium style={{ color: "grey" }} />}
                    />
                  </Grid>
                  <Grid size={3}>
                    <TextInput label="Année" name="year" fullWidth />
                  </Grid>
                  <Grid size={6} alignContent="center">
                    <PositionInput name="position" />
                  </Grid>
                  <Grid size={12}>
                    <ListInput label="Pilotes" name="pilots" />
                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <TextInput
                      label="Fabricant"
                      name="manufacturer"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <TextInput label="Référence" name="reference" fullWidth />
                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <TextInput
                      label="Prix d'achat"
                      name="purchasePrice"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <TextInput
                      label="Emplacement"
                      name="storageLocation"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ sm: 6, xs: 12 }}>
                    <TextInput label="Propriétaire" name="owner" fullWidth />
                  </Grid>
                  <Grid size={12}>
                    <TextInput
                      label="Détails"
                      name="details"
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Grid>
            <Grid size={12}>
              {globalErrors && (
                <Zoom in={globalErrors && globalErrors.length > 0}>
                  <Alert severity="error">
                    <AlertTitle>
                      Erreur(s) lors de l'envoi du formulaire
                    </AlertTitle>
                    {globalErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </Alert>
                </Zoom>
              )}
            </Grid>
            <Grid size={12}>
              <CardActions>
                <Box flexGrow={1}>
                  <Button onClick={() => cancelEdit()}>Annuler</Button>
                  <LoadingButton
                    loading={isSubmitting}
                    loadingPosition="start"
                    startIcon={<Save />}
                    style={{ marginLeft: "10px" }}
                    type="submit"
                    variant="contained"
                    onClick={clearGlobalErrors}
                  >
                    {isAddMode ? "Ajouter" : "Sauvegarder"}
                  </LoadingButton>
                </Box>
                <Box>
                  {!isAddMode && (
                    <>
                      <IconButton
                        color="secondary"
                        onClick={() => setOpenDeleteConfirm(true)}
                      >
                        <Delete />
                      </IconButton>
                      <Dialog
                        open={openDeleteConfirm}
                        onClose={handleCancelDelete}
                      >
                        <DialogContent>
                          <DialogContentText id="alert-dialog-description">
                            Êtes-vous sûr de vouloir supprimer ce véhicule ?
                          </DialogContentText>
                          {deleteStatus.errors && (
                            <DialogContentText>
                              <Zoom in={deleteStatus.errors.length > 0}>
                                <Alert
                                  severity="error"
                                  style={{ marginTop: 10 }}
                                >
                                  <AlertTitle>
                                    Impossible de supprimer le véhicule
                                  </AlertTitle>
                                  {deleteStatus.errors.map((error) => (
                                    <li key={error}>{error}</li>
                                  ))}
                                </Alert>
                              </Zoom>
                            </DialogContentText>
                          )}
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={handleCancelDelete}>Annuler</Button>
                          <LoadingButton
                            onClick={handleDelete}
                            autoFocus
                            startIcon={<DeleteForever />}
                            loading={deleteStatus.inProgress}
                            loadingPosition="start"
                            variant="contained"
                          >
                            Confirmer
                          </LoadingButton>
                        </DialogActions>
                      </Dialog>
                    </>
                  )}
                </Box>
              </CardActions>
            </Grid>
          </Grid>
        </Card>
      </form>
    </FormProvider>
  );
};

export default CarForm;
